import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { finalize, map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SessionStorageService } from '../session-storage.service';
import { RefreshTokenResponse } from './models';
import { TokenHelperService } from './token-helper.service';

@Injectable({ providedIn: 'root' })
export class TokenService {
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly tokenHelperService = inject(TokenHelperService);
  private readonly httpClient = inject(HttpClient);

  private tokenRefreshRequest: Observable<RefreshTokenResponse> | null = null;

  getAccessToken() {
    return JSON.parse(this.sessionStorageService.get('token') ?? null);
  }

  setAccessToken(token: string) {
    this.sessionStorageService.set('token', JSON.stringify(token));
  }

  getRefreshToken() {
    return JSON.parse(this.sessionStorageService.get('refreshToken') ?? null);
  }

  setRefreshToken(token: string) {
    this.sessionStorageService.set('refreshToken', JSON.stringify(token));
  }

  removeTokens() {
    this.sessionStorageService.remove('token');
    this.sessionStorageService.remove('refreshToken');
  }

  isTokenExpired(token: string) {
    const offsetSeconds = 30;

    return this.tokenHelperService.isTokenExpired(token, offsetSeconds);
  }

  isTokenValid(token: string | null) {
    const decodedToken = this.tokenHelperService.decodeToken(token);

    return decodedToken !== null;
  }

  refreshToken() {
    return this.httpClient.post<RefreshTokenResponse>(
      `${environment.zuul}user/login/refresh-token`,
      {},
      {
        headers: {
          Authorization: `Bearer ${this.getRefreshToken()}`,
        },
      },
    );
  }

  tokenGetter(): Observable<null | RefreshTokenResponse> {
    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return of(null);
    }

    if (!this.isTokenValid(token) || this.isTokenExpired(token)) {
      return this.requestNewToken();
    }

    return of({ token, refreshToken });
  }

  private requestNewToken() {
    if (this.tokenRefreshRequest) {
      return this.tokenRefreshRequest;
    }

    this.tokenRefreshRequest = this.refreshToken().pipe(
      map((response) => {
        this.setRefreshToken(response.refreshToken);
        this.setAccessToken(response.token);

        return response;
      }),
      finalize(() => {
        this.tokenRefreshRequest = null;
      }),
    );

    return this.tokenRefreshRequest;
  }
}
